export type CurvedTextPaintMode = 'fill' | 'stroke';

interface CurvedTextContext {
  textAlign: CanvasTextAlign;
  measureText(text: string): TextMetrics;
  save(): void;
  restore(): void;
  translate(x: number, y: number): void;
  rotate(angle: number): void;
  fillText(text: string, x: number, y: number): void;
  strokeText(text: string, x: number, y: number): void;
}

export interface CurvedTextBounds {
  width: number;
  height: number;
  centerOffsetX: number;
  centerOffsetY: number;
}

/** Approximate the painted arc bounds for editor selection and hit testing. */
export function curvedTextBounds(textWidth: number, fontSize: number, curve: number | null | undefined): CurvedTextBounds {
  const strength = normalizeCurve(curve);
  const width = Math.max(1, textWidth);
  const glyphPadding = Math.max(1, fontSize * 0.58);
  if (Math.abs(strength) < 0.5) {
    return { width, height: fontSize * 1.25, centerOffsetX: 0, centerOffsetY: 0 };
  }
  const span = Math.abs(strength) * Math.PI / 180;
  const direction = Math.sign(strength) || 1;
  const radius = Math.max(fontSize * 0.45, width / Math.max(0.01, span));
  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (let index = 0; index <= 96; index++) {
    const angle = -span / 2 + (span * index) / 96;
    const px = radius * Math.sin(angle);
    const py = direction * radius * (1 - Math.cos(angle));
    minX = Math.min(minX, px);
    maxX = Math.max(maxX, px);
    minY = Math.min(minY, py);
    maxY = Math.max(maxY, py);
  }
  return {
    width: maxX - minX + glyphPadding,
    height: maxY - minY + fontSize * 1.15,
    centerOffsetX: (minX + maxX) / 2,
    centerOffsetY: (minY + maxY) / 2,
  };
}

/**
 * Paint one line on a true circular arc up to a complete ±360° loop. A
 * missing/zero curve delegates to the native whole-line path, preserving the
 * exact legacy rendering fast path.
 */
export function drawCurvedTextLine(
  context: CurvedTextContext,
  text: string,
  x: number,
  y: number,
  fontSize: number,
  curve: number | null | undefined,
  mode: CurvedTextPaintMode,
): void {
  const strength = normalizeCurve(curve);
  if (Math.abs(strength) < 0.5 || text.length < 2) {
    if (mode === 'stroke') context.strokeText(text, x, y);
    else context.fillText(text, x, y);
    return;
  }

  const glyphs = Array.from(text);
  const widths = glyphs.map(glyph => context.measureText(glyph).width);
  const totalWidth = Math.max(1, widths.reduce((sum, width) => sum + width, 0));
  const originalAlign = context.textAlign;
  let cursor = originalAlign === 'center' ? x - totalWidth / 2 : originalAlign === 'right' || originalAlign === 'end' ? x - totalWidth : x;
  const midpoint = cursor + totalWidth / 2;
  const span = Math.abs(strength) * Math.PI / 180;
  const direction = Math.sign(strength) || 1;
  const radius = Math.max(fontSize * 0.45, totalWidth / Math.max(0.01, span));

  context.save();
  context.textAlign = 'center';
  for (let index = 0; index < glyphs.length; index++) {
    const width = widths[index]!;
    const center = cursor + width / 2;
    const progress = Math.max(-0.5, Math.min(0.5, (center - midpoint) / totalWidth));
    const angle = span * progress;
    const offsetX = radius * Math.sin(angle);
    const offsetY = direction * radius * (1 - Math.cos(angle));
    context.save();
    context.translate(midpoint + offsetX, y + offsetY);
    context.rotate(direction * angle);
    if (mode === 'stroke') context.strokeText(glyphs[index]!, 0, 0);
    else context.fillText(glyphs[index]!, 0, 0);
    context.restore();
    cursor += width;
  }
  context.restore();
}

function normalizeCurve(curve: number | null | undefined): number {
  return Number.isFinite(curve) ? Math.max(-360, Math.min(360, curve!)) : 0;
}
